/**
 * Helpers for reasoning about challenge "kinds" in the admin UI.
 *
 * `answerType` only describes how a participant *submits an answer*. For the
 * minigames and roadblock challenges the `type` itself defines the whole
 * experience (the player taps a grid, slides a puzzle, a facilitator marks it
 * done…), so showing "Answer Type: Text" for those is meaningless and
 * confusing — the admin UI hides it there.
 */

/** The five interactive minigame challenge types. */
export const MINIGAME_TYPES = [
  'memory_match',
  'slot_machine',
  'guess_word',
  'guess_song',
  'related_words',
] as const;

/**
 * Every challenge type the API accepts, in the order the admin UI lists them.
 * Single source of truth — the `ChallengeType` union in
 * `config/objects/challenges.tsx` and every type `<Select>` derive from this.
 */
export const CHALLENGE_TYPES = [
  'riddle',
  'counting',
  'exploration',
  'search',
  'observation',
  'photo',
  'video',
  'puzzle',
  'roadblock',
  ...MINIGAME_TYPES,
  'finale',
] as const;

/** API challenge-type union derived from {@link CHALLENGE_TYPES}. */
export type ChallengeTypeValue = (typeof CHALLENGE_TYPES)[number];

/** Human labels for every challenge type. */
export const CHALLENGE_TYPE_LABELS: Record<ChallengeTypeValue, string> = {
  riddle: 'Trivia',
  counting: 'Counting',
  exploration: 'Exploration',
  search: 'Search',
  observation: 'Observation',
  photo: 'Photo',
  video: 'Video',
  puzzle: 'Puzzle',
  roadblock: 'Roadblock',
  memory_match: 'Memory Match',
  slot_machine: 'Slot Machine',
  guess_word: 'Guess the Word',
  guess_song: 'Guess the Song',
  related_words: 'Related Words',
  finale: 'Finale',
};

/**
 * Ordered `{ value, label }` options for challenge-type selects. Derived from
 * {@link CHALLENGE_TYPES} + {@link CHALLENGE_TYPE_LABELS} so a new type only
 * needs one entry in each.
 */
export const CHALLENGE_TYPE_OPTIONS: ReadonlyArray<{
  value: ChallengeTypeValue;
  label: string;
}> = CHALLENGE_TYPES.map((value) => ({
  value,
  label: CHALLENGE_TYPE_LABELS[value],
}));

/** Type guard: `type` is one of the API challenge types. */
export function isKnownChallengeType(type: string): type is ChallengeTypeValue {
  return (CHALLENGE_TYPES as readonly string[]).includes(type);
}

/** True for the five minigame types. */
export function isMinigameType(type: string): boolean {
  return (MINIGAME_TYPES as readonly string[]).includes(type);
}

/**
 * Whether `answerType` is meaningful for this challenge type. False for
 * roadblock + the minigames, where the `type` fully drives the experience.
 */
export function hasMeaningfulAnswerType(type: string): boolean {
  return type !== 'roadblock' && !isMinigameType(type);
}

/** Humanized label for a challenge type (falls back to the raw value). */
export function challengeTypeLabel(type: string): string {
  return isKnownChallengeType(type) ? CHALLENGE_TYPE_LABELS[type] : type;
}

/**
 * Whether this challenge type is answered by typing/selecting a text answer.
 * Puzzle, roadblock, and the minigames are not — they're played or
 * facilitator-completed, so the form must not require (or show) an answer.
 * (Photo/video are AI-validated and handled separately by answerType.)
 */
export function hasTextAnswer(type: string): boolean {
  return type !== 'puzzle' && type !== 'roadblock' && !isMinigameType(type);
}
